import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

export type FontScale = 1 | 1.1 | 1.2 | 1.3

export type AccessibilityPreferences = {
  fontScale: FontScale
  highContrast: boolean
  grayscale: boolean
  reduceMotion: boolean
  underlineLinks: boolean
  largeCursor: boolean
  increasedLineHeight: boolean
}

export type AccessibilityPersistence = {
  load: () => Promise<Partial<AccessibilityPreferences> | null>
  save: (preferences: AccessibilityPreferences) => Promise<void>
}

type AccessibilityContextValue = {
  isOpen: boolean
  preferences: AccessibilityPreferences
  close: () => void
  open: () => void
  reset: () => void
  setPreference: <Key extends keyof AccessibilityPreferences>(
    key: Key,
    value: AccessibilityPreferences[Key]
  ) => void
  toggle: () => void
}

const STORAGE_KEY = 'nosi-accessibility-preferences-v1'

const systemPrefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches

const createDefaults = (): AccessibilityPreferences => ({
  fontScale: 1,
  highContrast: false,
  grayscale: false,
  reduceMotion: systemPrefersReducedMotion(),
  underlineLinks: false,
  largeCursor: false,
  increasedLineHeight: false,
})

const normalizePreferences = (
  value?: Partial<AccessibilityPreferences> | null
): AccessibilityPreferences => {
  const defaults = createDefaults()
  const fontScale = [1, 1.1, 1.2, 1.3].includes(Number(value?.fontScale))
    ? (Number(value?.fontScale) as FontScale)
    : defaults.fontScale

  return {
    fontScale,
    highContrast: Boolean(value?.highContrast),
    grayscale: Boolean(value?.grayscale),
    reduceMotion:
      typeof value?.reduceMotion === 'boolean' ? value.reduceMotion : defaults.reduceMotion,
    underlineLinks: Boolean(value?.underlineLinks),
    largeCursor: Boolean(value?.largeCursor),
    increasedLineHeight: Boolean(value?.increasedLineHeight),
  }
}

const readLocalPreferences = () => {
  if (typeof window === 'undefined') {
    return createDefaults()
  }

  try {
    const value = window.localStorage.getItem(STORAGE_KEY)
    return value ? normalizePreferences(JSON.parse(value)) : createDefaults()
  } catch {
    return createDefaults()
  }
}

const AccessibilityContext = createContext<AccessibilityContextValue | undefined>(undefined)

type AccessibilityProviderProps = {
  children: ReactNode
  effectTarget?: string
  persistence?: AccessibilityPersistence
}

export const AccessibilityProvider: FC<AccessibilityProviderProps> = ({
  children,
  effectTarget = '#root',
  persistence,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [preferences, setPreferences] = useState<AccessibilityPreferences>(readLocalPreferences)
  const [remoteReady, setRemoteReady] = useState(!persistence)
  const userRevisionRef = useRef(0)

  useEffect(() => {
    if (!persistence) {
      return
    }

    let active = true
    const revisionAtStart = userRevisionRef.current

    persistence
      .load()
      .then((remotePreferences) => {
        if (active && remotePreferences && userRevisionRef.current === revisionAtStart) {
          setPreferences(normalizePreferences(remotePreferences))
        }
      })
      .catch(() => {
        // A failed read never causes the local value to overwrite the remote preference.
      })
      .finally(() => {
        if (active) {
          setRemoteReady(true)
        }
      })

    return () => {
      active = false
    }
  }, [persistence])

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences))
    } catch {
      // Local storage may be unavailable in restricted browser contexts.
    }

    const target = document.querySelector<HTMLElement>(effectTarget) || document.documentElement
    const classes: Array<[string, boolean]> = [
      ['nosi-a11y-high-contrast', preferences.highContrast],
      ['nosi-a11y-grayscale', preferences.grayscale],
      ['nosi-a11y-reduce-motion', preferences.reduceMotion],
      ['nosi-a11y-underline-links', preferences.underlineLinks],
      ['nosi-a11y-large-cursor', preferences.largeCursor],
      ['nosi-a11y-line-height', preferences.increasedLineHeight],
    ]

    const markerTargets = target === document.body ? [target] : [target, document.body]
    markerTargets.forEach((markerTarget) =>
      classes.forEach(([className, enabled]) => markerTarget.classList.toggle(className, enabled))
    )
    document.documentElement.style.setProperty('--nosi-font-scale', String(preferences.fontScale))

    return () => {
      markerTargets.forEach((markerTarget) =>
        classes.forEach(([className]) => markerTarget.classList.remove(className))
      )
      document.documentElement.style.removeProperty('--nosi-font-scale')
    }
  }, [effectTarget, preferences])

  useEffect(() => {
    if (!persistence || !remoteReady || userRevisionRef.current === 0) {
      return
    }

    const timeout = window.setTimeout(() => {
      persistence.save(preferences).catch(() => {
        // The local preference remains authoritative until a later successful save.
      })
    }, 450)

    return () => window.clearTimeout(timeout)
  }, [persistence, preferences, remoteReady])

  const markUserChange = useCallback(() => {
    userRevisionRef.current += 1
  }, [])

  const setPreference = useCallback(
    <Key extends keyof AccessibilityPreferences>(
      key: Key,
      value: AccessibilityPreferences[Key]
    ) => {
      markUserChange()
      setPreferences((current) => ({...current, [key]: value}))
    },
    [markUserChange]
  )

  const reset = useCallback(() => {
    markUserChange()
    setPreferences(createDefaults())
  }, [markUserChange])

  const close = useCallback(() => setIsOpen(false), [])
  const open = useCallback(() => setIsOpen(true), [])
  const toggle = useCallback(() => setIsOpen((current) => !current), [])

  const value = useMemo<AccessibilityContextValue>(
    () => ({
      isOpen,
      preferences,
      close,
      open,
      reset,
      setPreference,
      toggle,
    }),
    [close, isOpen, open, preferences, reset, setPreference, toggle]
  )

  return <AccessibilityContext.Provider value={value}>{children}</AccessibilityContext.Provider>
}

export const useAccessibility = () => {
  const context = useContext(AccessibilityContext)
  if (!context) {
    throw new Error('useAccessibility must be used inside AccessibilityProvider')
  }

  return context
}
